import React, { useState, useEffect } from 'react';
import apiService, {
    errorHelpers,
    formatHelpers,
    validationHelpers,
    paymentHelpers,
    storageHelpers
} from '../../../utils/apiService';

const VerificationForm = ({ onVerificationComplete, compact = false }) => {
    // ── Component State ───────────────────────────────────────────────────────────
    const [step, setStep] = useState('input'); // 'input' | 'confirm' | 'payment' | 'waiting' | 'result'
    const [certificateNumber, setCertificateNumber] = useState('');
    const [verificationSettings, setVerificationSettings] = useState(null);
    const [verificationData, setVerificationData] = useState(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(null);
    const [transactionId, setTransactionId] = useState(null);
    const [paymentWindow, setPaymentWindow] = useState(null);
    const origin = window.location.origin;
    // ── Fetch verification settings on mount ─────────────────────────
    useEffect(() => {
        loadVerificationSettings();
    }, []);

    const loadVerificationSettings = async () => {
        try {
            const response = await apiService.verification.getSettings();
            setVerificationSettings(response.data);
        } catch (error) {
            console.error('Failed to load verification settings:', error);
        }
    };

    // ── Handlers ─────────────────────────────────────────────────────────
    const handleCertificateNumberChange = (e) => {
        const value = e.target.value.toUpperCase();
        setCertificateNumber(value);
        setAlert(null);
    };

    const handleVerifyClick = async () => {
        if (!certificateNumber.trim()) {
            setAlert({ type: 'error', message: 'Please enter a certificate number.' });
            return;
        }
        if (!validationHelpers.validateCertificateNumber(certificateNumber)) {
            setAlert({ type: 'error', message: 'Please enter a valid certificate number.' });
            return;
        }

        setLoading(true);
        setAlert(null);

        try {
            const response = await apiService.verification.checkCertificate(certificateNumber);

            if (response.data.status === 'already_paid') {
                setResult({
                    type: 'success',
                    certificate: response.data.certificate,
                    message: 'Certificate already verified.',
                    authenticationDate: response.data.authentication_date
                });
                setStep('result');
                if (onVerificationComplete) onVerificationComplete();
            } else if (response.data.status === 'payment_required') {
                setVerificationData(response.data);
                setStep('confirm');
            }
        } catch (error) {
            const errorMessage = errorHelpers.getErrorMessage(error);
            setAlert({ type: 'error', message: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmAndProceed = () => {
        setStep('payment');
    };

    const handlePayment = async () => {
        setLoading(true);
        setAlert(null);

        try {
            const response = await apiService.verification.processPayment(certificateNumber);

            if (response.data.status === 'payment_initialized') {
                const { payment_url, payment_data, transaction_id: txId } = response.data;
                setTransactionId(txId);

                const popup = await apiService.payment.openPaymentPopup(payment_data, payment_url);
                setPaymentWindow(popup);
                setStep('waiting');
            } else if (response.data.status === 'not_found') {
                setResult({
                    type: 'not_found',
                    message: 'Certificate not found in our database.',
                    paymentReference: response.data.payment_reference,
                    amountPaid: response.data.amount_paid,
                    currency: response.data.currency
                });
                setAlert({ type: 'error', message: 'Certificate not found.' });
                setStep('result');
            }
        } catch (error) {
            const errorMessage = errorHelpers.getErrorMessage(error);
            setAlert({ type: 'error', message: errorMessage });
            setStep('payment');
        } finally {
            setLoading(false);
        }
    };

    const handleCheckStatus = async () => {
        if (!transactionId) return;
        setLoading(true);
        setAlert(null);

        try {
            const resp = await apiService.verification.checkVerificationStatus(transactionId);

            if (resp.data && resp.data.status === 'paid' && resp.data.verification_status === 'success') {
                setResult({
                    type: 'success',
                    certificate: resp.data.certificate,
                    message: resp.data.message || 'Payment successful! Certificate verified.',
                    paymentReference: resp.data.payment_reference,
                    amountPaid: resp.data.amount_paid,
                    currency: resp.data.currency,
                    verificationTemplate: resp.data.verification_template
                });
                setStep('result');
                if (onVerificationComplete) onVerificationComplete();
                if (paymentWindow) paymentWindow.close();

                try {
                    const key = `payment_session_${transactionId}`;
                    localStorage.removeItem(key);
                } catch (e) {
                    console.warn('Could not clear payment session:', e);
                }
                setLoading(false);
                return;
            }

            if (resp.data.status === 'paid' && resp.data.verification_status === 'not_found') {
                setResult({
                    type: 'not_found',
                    message: resp.data.message || 'Payment completed but certificate not found in database.',
                    paymentReference: resp.data.payment_reference,
                    amountPaid: resp.data.amount_paid,
                    currency: resp.data.currency
                });
                setStep('result');
                if (paymentWindow) paymentWindow.close();
                setLoading(false);
                return;
            }

            if (resp.data.status === 'failed') {
                setAlert({
                    type: 'error',
                    message: resp.data.message || 'Payment failed or was cancelled. Please try again.'
                });
                setStep('payment');
                if (paymentWindow) paymentWindow.close();
                setLoading(false);
                return;
            }

            setAlert({
                type: 'info',
                message: resp.data.message || 'Payment still pending. Please complete the payment.'
            });

        } catch (err) {
            console.error('Status check error:', err);
            /*setAlert({
                type: 'error',
                message: 'Unable to check payment status. Please try again or contact support if the issue persists.'
            });*/
        } finally {
            setLoading(false);
        }
    };

    // Function to print verification template
    const handlePrintTemplate = async () => {
        if (!result?.verificationTemplate) return;

        const template = result.verificationTemplate;
        const certificate = result.certificate;

        const printWindow = window.open('', '_blank', 'width=800,height=600');

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Certificate Verification - ${certificate.certificate_number}</title>
                <style>
                    body {
                        font-family: 'Times New Roman', serif;
                        font-size: 14px;
                        line-height: 1.6;
                        color: #000;
                        max-width: 800px;
                        margin: 0 auto;
                        padding: 20px;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 30px;
                    }
                    .university-logo {
                        width: 80px;
                        height: 80px;
                        margin: 0 auto 15px;
                        display: block;
                    }
                    .university-name {
                        font-size: 18px;
                        font-weight: bold;
                        margin-bottom: 5px;
                    }
                    .university-address {
                        font-size: 12px;
                        margin-bottom: 20px;
                    }
                    .ref-date {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 20px;
                        font-weight: bold;
                    }
                    .recipient {
                        margin-bottom: 20px;
                        line-height: 1.4;
                    }
                    .recipient-line {
                        margin-bottom: 2px;
                    }
                    .recipient-company {
                        font-weight: bold;
                        margin-bottom: 5px;
                    }
                    .subject {
                        text-align: center;
                        font-weight: bold;
                        text-decoration: underline;
                        margin: 20px 0;
                    }
                    .content {
                        margin: 20px 0;
                    }
                    .certificate-table {
                        width: 100%;
                        border-collapse: collapse;
                        margin: 20px 0;
                    }
                    .certificate-table th,
                    .certificate-table td {
                        border: 1px solid #000;
                        padding: 8px;
                        text-align: left;
                    }
                    .certificate-table th {
                        background-color: #f5f5f5;
                        font-weight: bold;
                    }
                    .signature {
                        margin-top: 30px;
                    }
                    .signature p {
                        margin: 5px 0;
                    }
                    @media print {
                        body { margin: 0; }
                        .no-print { display: none; }
                    }
                        .signature-container {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: 40px;
    page-break-inside: avoid;
    break-inside: avoid;
}

.signature-section {
    flex: 1;
    page-break-inside: avoid;
    break-inside: avoid;
}

.stamp-section {
    margin-left: 20px;
    flex-shrink: 0;
    page-break-inside: avoid;
    break-inside: avoid;
}

@media print {
    body { 
        margin: 0; 
        font-size: 12px;
        line-height: 1.4;
    }
    .no-print { 
        display: none; 
    }
    .signature-container {
        page-break-inside: avoid;
        break-inside: avoid;
        margin-top: 20px;
    }
    .signature-section,
    .stamp-section {
        page-break-inside: avoid;
        break-inside: avoid;
    }
    @page {
        margin: 0.5in;
        size: A4;
    }
}
                </style>
            </head>
            <body>
                <div class="no-print" style="text-align: center; margin-bottom: 20px;">
                    <button onclick="window.print()" style="padding: 10px 20px; background: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer;">
                        🖨️ Print Document
                    </button>
                    <button onclick="window.close()" style="padding: 10px 20px; background: #6c757d; color: white; border: none; border-radius: 5px; cursor: pointer; margin-left: 10px;">
                        ✖️ Close
                    </button>
                </div>
                
                <div class="header">
<img src="${template.assets?.logo || '/images/oou_logo.png'}" alt="OOU Logo" class="university-logo" onerror="this.style.display='none'">                    <div class="university-name">${template.header.institution}</div>
                    <div class="university-address">${template.header.address}</div>
                </div>
                
                <div class="ref-date">
                    <div><strong>Our Ref: ${template.header.ref_number}</strong></div>
                    <div><strong>Date: ${template.header.date}</strong></div>
                </div>
                
                <div class="recipient">
                    <div class="recipient-company">${template.recipient.company}</div>
                    ${template.recipient.address.map(line => `<div class="recipient-line">${line}</div>`).join('')}
                </div>
                
                <div class="subject">RE: ${template.subject}</div>
                
                <div class="content">
                            <div class="student-details">
                                <table style="width: 100%; margin: 20px 0; border: none;">
                                    <tr>
                                        <td style="border: none; padding: 5px 0; width: 150px;"><strong>NAME:</strong></td>
                                        <td style="border: none; padding: 5px 0; padding-left: 20px;">${template.content.student_info.name}</td>
                                    </tr>
                                    <tr>
                                        <td style="border: none; padding: 5px 0;"><strong>MATRIC NO:</strong></td>
                                        <td style="border: none; padding: 5px 0; padding-left: 20px;">${template.content.student_info.matric_number}</td>
                                    </tr>
                                    <tr>
                                        <td style="border: none; padding: 5px 0;"><strong>COURSE:</strong></td>
                                        <td style="border: none; padding: 5px 0; padding-left: 20px;">${template.content.student_info.course}</td>
                                    </tr>
                                </table>
                            </div>
                            
                            <p style="margin: 30px 0 20px 0;">${template.content.confirmation_text}</p>
                            <p style="margin: 20px 0;">${template.content.closing}</p>
                        </div>
                
               <div class="signature">
    <div class="signature-container">
        <div class="signature-section">
            ${template.signature.signature_image ?
                `<img src="${template.signature.signature_image}" alt="Signature" style="max-width: 150px; height: auto; margin-bottom: 10px;" onerror="this.style.display='none'">` :
                '<div style="height: 60px; border-bottom: 1px solid #000; width: 200px; margin-bottom: 10px;"></div>'
            }
            <p><em><strong>${template.signature.name}</strong></em></p>
            <p><em><strong>${template.signature.title}</strong></em></p>
            <p><em><strong>${template.signature.for}</strong></em></p>
            <p style="font-size: 12px;">${template.signature.email}</p>
        </div>
        <div class="stamp-section">
            ${template.signature.stamp_image ?
                `<img src="${template.signature.stamp_image}" alt="Official Stamp" style="max-width: 120px; height: auto;" onerror="this.style.display='none'">` :
                '<div style="width: 120px; height: 120px; border: 2px solid #000; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10px; text-align: center;">OFFICIAL<br>STAMP</div>'
            }
        </div>
    </div>
</div>
            </body>
            </html>
        `);

        printWindow.document.close();
        printWindow.focus();
    };

    // ── Rendering Steps ────────────────────────────────────────────────────────

    const renderInputStep = () => (
        <div className="verification-step">
            <div className="step-header">
                <div className="step-icon">🔍</div>
                <h2>Certificate Verification</h2>
                <p>Enter your certificate number to begin the verification process</p>
            </div>

            <div className="form-group">
                <label htmlFor="cert-input" className="form-label">
                    Certificate Number <span className="required">*</span>
                </label>
                <div className="input-group">
                    <span className="input-prefix">📜</span>
                    <input
                        id="cert-input"
                        type="text"
                        value={certificateNumber}
                        onChange={handleCertificateNumberChange}
                        className="form-input"
                        placeholder="e.g., UG00000000"
                        disabled={loading}
                    />
                </div>
                <div className="input-help">
                    Enter the certificate number exactly as it appears on your certificate
                </div>
            </div>

            {verificationSettings && (
                <div className="info-card">
                    <div className="info-header">
                        <span className="info-icon">ℹ️</span>
                        <h4>Verification Information</h4>
                    </div>
                    <div className="info-grid">
                        <div className="info-item">
                            <span className="info-label">Verification Fee</span>
                            <span className="info-value">
                                {formatHelpers.formatCurrency(
                                    verificationSettings.verification_fee,
                                    verificationSettings.currency
                                )}
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Processing Time</span>
                            <span className="info-value">Instant</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Payment Method</span>
                            <span className="info-value">Card</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Official Letter</span>
                            <span className="info-value">Printable PDF</span>
                        </div>
                    </div>
                </div>
            )}

            <button
                onClick={handleVerifyClick}
                className="btn btn-primary btn-large"
                disabled={loading || !certificateNumber.trim()}
            >
                {loading ? (
                    <>
                        <span className="spinner"></span>
                        Checking Certificate...
                    </>
                ) : (
                    <>
                        <span className="btn-icon">🔍</span>
                        Verify Certificate
                    </>
                )}
            </button>
        </div>
    );

    const renderConfirmStep = () => (
        <div className="verification-step">
            <div className="step-header">
                <div className="step-icon">✅</div>
                <h2>Confirm Details</h2>
                <p>Please review your information before proceeding to payment</p>
            </div>

            <div className="confirmation-card">
                <div className="confirmation-row">
                    <span className="confirmation-label">Certificate Number</span>
                    <span className="confirmation-value">{certificateNumber}</span>
                </div>
                <div className="confirmation-row">
                    <span className="confirmation-label">Verification Fee</span>
                    <span className="confirmation-value fee">
                        {formatHelpers.formatCurrency(
                            verificationSettings?.verification_fee,
                            verificationSettings?.currency
                        )}
                    </span>
                </div>
                <div className="confirmation-row">
                    <span className="confirmation-label">What you'll receive</span>
                    <span className="confirmation-value">Official verification letter</span>
                </div>
            </div>

            <div className="button-group">
                <button
                    onClick={() => setStep('input')}
                    className="btn btn-secondary"
                    disabled={loading}
                >
                    <span className="btn-icon">←</span>
                    Edit Number
                </button>
                <button
                    onClick={handleConfirmAndProceed}
                    className="btn btn-primary"
                    disabled={loading}
                >
                    <span className="btn-icon">💳</span>
                    Proceed to Payment
                </button>
            </div>
        </div>
    );

    const renderPaymentStep = () => (
        <div className="verification-step">
            <div className="step-header">
                <div className="step-icon">💳</div>
                <h2>Complete Payment</h2>
                <p>Secure payment processing through our trusted payment gateway</p>
            </div>

            <div className="payment-info">
                <div className="payment-amount">
                    <span className="amount-label">Amount to Pay</span>
                    <span className="amount-value">
                        {formatHelpers.formatCurrency(
                            verificationSettings?.verification_fee,
                            verificationSettings?.currency
                        )}
                    </span>
                </div>
                <div className="payment-methods">
                    <span className="payment-label">Accepted Methods</span>
                    <div className="payment-icons">
                        <span className="payment-icon">💳</span>
                        <span className="payment-icon">🏦</span>
                        <span className="payment-icon">📱</span>
                    </div>
                </div>
            </div>

            <button
                onClick={handlePayment}
                className="btn btn-success btn-large"
                disabled={loading}
            >
                {loading ? (
                    <>
                        <span className="spinner"></span>
                        Initializing Payment...
                    </>
                ) : (
                    <>
                        <span className="btn-icon">🔒</span>
                        Open Secure Payment Gateway
                    </>
                )}
            </button>

            <div className="security-note">
                <span className="security-icon">🔒</span>
                <span>Your payment is secured with bank-level encryption</span>
            </div>
        </div>
    );

    const renderWaitingStep = () => (
        <div className="verification-step">
            <div className="step-header">
                <div className="step-icon animated">⏳</div>
                <h2>Payment in Progress</h2>
                <p>Complete your payment in the secure popup window</p>
            </div>

            <div className="waiting-content">
                <div className="progress-indicator">
                    <div className="progress-step completed">
                        <span className="progress-icon">✓</span>
                        <span className="progress-label">Certificate Found</span>
                    </div>
                    <div className="progress-step active">
                        <span className="progress-icon">💳</span>
                        <span className="progress-label">Payment Processing</span>
                    </div>
                    <div className="progress-step">
                        <span className="progress-icon">📄</span>
                        <span className="progress-label">Verification Complete</span>
                    </div>
                </div>

                {transactionId && (
                    <div className="transaction-info">
                        <span className="transaction-label">Transaction ID</span>
                        <span className="transaction-id">{transactionId.replace(/(.{4})/g, '$1-').slice(0, -1)}</span>
                    </div>
                )}

                <button
                    onClick={handleCheckStatus}
                    className="btn btn-primary"
                    disabled={loading}
                >
                    {loading ? (
                        <>
                            <span className="spinner"></span>
                            Checking Status...
                        </>
                    ) : (
                        <>
                            <span className="btn-icon">🔄</span>
                            Check Payment Status
                        </>
                    )}
                </button>

                <div className="help-section">
                    <h4>Need Help?</h4>
                    <ul className="help-list">
                        <li>Ensure you complete the payment in the popup window</li>
                        <li>If the popup was blocked, check your browser settings</li>
                        <li>Contact support if payment was completed but status shows pending</li>
                    </ul>
                </div>
            </div>
        </div>
    );

    const renderResultStep = () => {
        if (!result) return null;

        return (
            <div className="verification-step">
                {result.type === 'success' && (
                    <>
                        <div className="step-header success">
                            <div className="step-icon">✅</div>
                            <h2>Certificate Verified Successfully!</h2>
                            <p>Your certificate has been authenticated and verified, proceed to the verification history to print</p>
                        </div>

                        <div className="result-card success">
                            <div className="result-grid">
                                <div className="result-item">
                                    <span className="result-label">Certificate Number</span>
                                    <span className="result-value">{certificateNumber}</span>
                                </div>
                                <div className="result-item">
                                    <span className="result-label">Student Name</span>
                                    <span className="result-value">{result.certificate?.student_name}</span>
                                </div>
                                <div className="result-item">
                                    <span className="result-label">Course</span>
                                    <span className="result-value">{result.certificate?.course}</span>
                                </div>
                                <div className="result-item">
                                    <span className="result-label">Graduation Date</span>
                                    <span className="result-value">{result.certificate?.graduation_date}</span>
                                </div>
                                <div className="result-item">
                                    <span className="result-label">Grade</span>
                                    <span className="result-value">{result.certificate?.grade}</span>
                                </div>

                            </div>
                        </div>

                        {result.verificationTemplate && (
                            <div className="template-section">
                                <div className="template-header">
                                    <h3>📄 Official Verification Letter</h3>
                                    <button
                                        onClick={handlePrintTemplate}
                                        className="btn btn-outline btn-sm"
                                    >
                                        <span className="btn-icon">🖨️</span>
                                        Print Letter
                                    </button>
                                </div>

                                <div className="template-preview">
                                    <div className="template-content">
                                        <div className="template-header-content">
                                            {result.verificationTemplate.assets?.logo && (
                                                <img
                                                    src={result.verificationTemplate.assets.logo}
                                                    alt="OOU Logo"
                                                    style={{
                                                        width: '60px',
                                                        height: '60px',
                                                        margin: '0 auto 10px',
                                                        display: 'block'
                                                    }}
                                                    onError={(e) => e.target.style.display = 'none'}
                                                />
                                            )}
                                            <h4>OLABISI ONABANJO UNIVERSITY</h4>
                                            <p>P.M.B. 2002, Ago-Iwoye, Ogun State, Nigeria</p>
                                        </div>

                                        <div className="template-ref">
                                            <span>Our Ref: {result.verificationTemplate.header.ref_number}</span>
                                            <span>Date: {result.verificationTemplate.header.date}</span>
                                        </div>

                                        <div className="template-recipient">
                                            <strong>{result.verificationTemplate.recipient.company}</strong>
                                            {result.verificationTemplate.recipient.address.map((line, index) => (
                                                <div key={index}>{line}</div>
                                            ))}
                                        </div>

                                        <div className="template-subject">
                                            <div class="subject">RE: CONFIRMATION OF PROFICIENCY IN ENGLISH LANGUAGE AS A MEDIUM OF INSTRUCTION AND COMMUNICATION</div>
                                        </div>

                                        <div className="template-body">
                                            <div className="student-details-preview">
                                                <div className="detail-row">
                                                    <strong>NAME:</strong>
                                                    <span style={{ marginLeft: '2rem' }}>{result.verificationTemplate.content.student_info.name}</span>
                                                </div>
                                                <div className="detail-row">
                                                    <strong>MATRIC NO:</strong>
                                                    <span style={{ marginLeft: '2rem' }}>{result.verificationTemplate.content.student_info.matric_number}</span>
                                                </div>
                                                <div className="detail-row">
                                                    <strong>COURSE:</strong>
                                                    <span style={{ marginLeft: '2rem' }}>{result.verificationTemplate.content.student_info.course}</span>
                                                </div>
                                            </div>

                                            <p style={{ margin: '1.5rem 0' }}>{result.verificationTemplate.content.confirmation_text}</p>
                                            <p style={{ margin: '1rem 0' }}>{result.verificationTemplate.content.closing}</p>
                                        </div>

                                        <div className="template-signature">
                                            <div className="signature-container-preview">
                                                <div className="signature-section-preview">
                                                    {result.verificationTemplate.signature?.signature_image && (
                                                        <img
                                                            src={result.verificationTemplate.signature.signature_image}
                                                            alt="Signature"
                                                            style={{
                                                                maxWidth: '120px',
                                                                height: 'auto',
                                                                marginBottom: '8px',
                                                                display: 'block'
                                                            }}
                                                            onError={(e) => e.target.style.display = 'none'}
                                                        />
                                                    )}
                                                    <p><em><strong>{result.verificationTemplate.signature.name}</strong></em></p>
                                                    <p><em><strong>{result.verificationTemplate.signature.title}</strong></em></p>
                                                    <p><em><strong>{result.verificationTemplate.signature.for}</strong></em></p>
                                                    <p style={{ fontSize: '0.75rem' }}>{result.verificationTemplate.signature.email}</p>
                                                </div>
                                                <div className="stamp-section-preview">
                                                    {result.verificationTemplate.signature?.stamp_image ? (
                                                        <img
                                                            src={result.verificationTemplate.signature.stamp_image}
                                                            alt="Official Stamp"
                                                            style={{
                                                                maxWidth: '80px',
                                                                height: 'auto'
                                                            }}
                                                            onError={(e) => e.target.style.display = 'none'}
                                                        />
                                                    ) : (
                                                        <div style={{
                                                            width: '80px',
                                                            height: '80px',
                                                            border: '1px solid #000',
                                                            borderRadius: '50%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '0.6rem',
                                                            textAlign: 'center'
                                                        }}>
                                                            OFFICIAL<br />STAMP
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>

                            </div>
                        )}
                    </>
                )}

                {result.type === 'not_found' && (
                    <>
                        <div className="step-header error">
                            <div className="step-icon">❌</div>
                            <h2>Certificate Not Found</h2>
                            <p>The certificate number was not found in our database</p>
                        </div>

                        <div className="result-card error">
                            <p>{result.message}</p>
                            <div className="result-grid">
                                <div className="result-item">
                                    <span className="result-label">Payment Reference</span>
                                    <span className="result-value">{result.paymentReference}</span>
                                </div>
                                <div className="result-item">
                                    <span className="result-label">Amount Paid</span>
                                    <span className="result-value">{result.amountPaid ? `₦${result.amountPaid}` : 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                <button
                    onClick={() => {
                        setCertificateNumber('');
                        setVerificationData(null);
                        setResult(null);
                        setStep('input');
                        setAlert(null);
                        setTransactionId(null);
                        if (paymentWindow) paymentWindow.close();
                    }}
                    className="btn btn-primary btn-large"
                >
                    <span className="btn-icon">🔍</span>
                    Verify Another Certificate
                </button>
            </div>
        );
    };

    // ── Main Render ────────────────────────────────────────────────────────────────
    return (
        <div className={`verification-form-container ${compact ? 'compact' : ''}`}>
            {alert && (
                <div className={`alert alert-${alert.type}`}>
                    <span className="alert-icon">
                        {alert.type === 'error' && '⚠️'}
                        {alert.type === 'success' && '✅'}
                        {alert.type === 'info' && 'ℹ️'}
                    </span>
                    <span className="alert-message">{alert.message}</span>
                    <button
                        onClick={() => setAlert(null)}
                        className="alert-close"
                    >
                        ×
                    </button>
                </div>
            )}

            <div className="verification-form">
                {step === 'input' && renderInputStep()}
                {step === 'confirm' && renderConfirmStep()}
                {step === 'payment' && renderPaymentStep()}
                {step === 'waiting' && renderWaitingStep()}
                {step === 'result' && renderResultStep()}
            </div>

            <style jsx>{`
                .verification-form-container {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 1rem;
                }

                .verification-form-container.compact {
                    max-width: 600px;
                }

                .alert {
                    display: flex;
                    align-items: center;
                    padding: 1rem;
                    border-radius: 12px;
                    margin-bottom: 2rem;
                    position: relative;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }

                .alert-error {
                    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
                    border: 1px solid #fecaca;
                    color: #dc2626;
                }

                .alert-success {
                    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
                    border: 1px solid #bbf7d0;
                    color: #16a34a;
                }

                .alert-info {
                    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
                    border: 1px solid #93c5fd;
                    color: #2563eb;
                }

                .alert-icon {
                    font-size: 1.25rem;
                    margin-right: 0.75rem;
                }

                .alert-message {
                    flex: 1;
                    font-weight: 500;
                }

                .alert-close {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    cursor: pointer;
                    color: inherit;
                    opacity: 0.7;
                    transition: opacity 0.2s;
                }

                .alert-close:hover {
                    opacity: 1;
                }

                .verification-form {
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                }

                .verification-step {
                    padding: 3rem 2rem;
                    text-align: center;
                }

                .step-header {
                    margin-bottom: 2.5rem;
                }

                .step-header.success {
                    color: #16a34a;
                }

                .step-header.error {
                    color: #dc2626;
                }

                .step-icon {
                    font-size: 4rem;
                    margin-bottom: 1rem;
                    display: block;
                }

                .step-icon.animated {
                    animation: pulse 2s infinite;
                }

                .step-header h2 {
                    font-size: 2rem;
                    font-weight: 700;
                    color: #1f2937;
                    margin-bottom: 0.75rem;
                }

                .step-header.success h2 {
                    color: #16a34a;
                }

                .step-header.error h2 {
                    color: #dc2626;
                }

                .step-header p {
                    font-size: 1.125rem;
                    color: #6b7280;
                    margin: 0;
                }

                .form-group {
                    margin-bottom: 2rem;
                    text-align: left;
                }

                .form-label {
                    display: block;
                    font-size: 1rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.5rem;
                }

                .required {
                    color: #dc2626;
                }

                .input-group {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .input-prefix {
                    position: absolute;
                    left: 1rem;
                    font-size: 1.25rem;
                    z-index: 1;
                }

                .form-input {
                    width: 100%;
                    padding: 1rem 1rem 1rem 3rem;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    font-size: 1.125rem;
                    font-weight: 500;
                    font-family: 'Courier New', monospace;
                    text-transform: uppercase;
                    transition: all 0.3s ease;
                    background: #f9fafb;
                }

                .form-input:focus {
                    outline: none;
                    border-color: #3b82f6;
                    background: white;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .form-input:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .input-help {
                    font-size: 0.875rem;
                    color: #6b7280;
                    margin-top: 0.5rem;
                }

                .info-card {
                    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                    border: 1px solid #bae6fd;
                    border-radius: 16px;
                    padding: 1.5rem;
                    margin-bottom: 2rem;
                    text-align: left;
                }

                .info-header {
                    display: flex;
                    align-items: center;
                    margin-bottom: 1rem;
                }

                .info-icon {
                    font-size: 1.5rem;
                    margin-right: 0.75rem;
                }

                .info-header h4 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #0c4a6e;
                    margin: 0;
                }

                .info-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                }

                .info-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem;
                    background: rgba(255, 255, 255, 0.7);
                    border-radius: 8px;
                }

                .info-label {
                    font-weight: 500;
                    color: #0c4a6e;
                }

                .info-value {
                    font-weight: 600;
                    color: #1e40af;
                }

                .confirmation-card {
                    background: #f8fafc;
                    border: 2px solid #e2e8f0;
                    border-radius: 16px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                }

                .confirmation-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem 0;
                    border-bottom: 1px solid #e2e8f0;
                }

                .confirmation-row:last-child {
                    border-bottom: none;
                }

                .confirmation-label {
                    font-weight: 500;
                    color: #475569;
                }

                .confirmation-value {
                    font-weight: 600;
                    color: #1e293b;
                }

                .confirmation-value.fee {
                    font-size: 1.25rem;
                    color: #059669;
                }

                .payment-info {
                    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
                    border: 1px solid #bbf7d0;
                    border-radius: 16px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                }

                .payment-amount {
                    text-align: center;
                    margin-bottom: 1.5rem;
                }

                .amount-label {
                    display: block;
                    font-size: 1rem;
                    color: #166534;
                    margin-bottom: 0.5rem;
                }

                .amount-value {
                    display: block;
                    font-size: 2.5rem;
                    font-weight: 700;
                    color: #059669;
                }

                .payment-methods {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .payment-label {
                    font-weight: 500;
                    color: #166534;
                }

                .payment-icons {
                    display: flex;
                    gap: 0.5rem;
                }

                .payment-icon {
                    font-size: 1.5rem;
                }

                .security-note {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    margin-top: 1rem;
                    font-size: 0.875rem;
                    color: #6b7280;
                }

                .security-icon {
                    color: #16a34a;
                }

                .waiting-content {
                    max-width: 500px;
                    margin: 0 auto;
                }

                .progress-indicator {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 2rem;
                    position: relative;
                }

                .progress-indicator::before {
                    content: '';
                    position: absolute;
                    top: 1.5rem;
                    left: 2rem;
                    right: 2rem;
                    height: 2px;
                    background: #e5e7eb;
                    z-index: 0;
                }

                .progress-step {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    position: relative;
                    z-index: 1;
                }

                .progress-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 3rem;
                    height: 3rem;
                    border-radius: 50%;
                    background: #f3f4f6;
                    color: #9ca3af;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                }

                .progress-step.completed .progress-icon {
                    background: #10b981;
                    color: white;
                }

                .progress-step.active .progress-icon {
                    background: #3b82f6;
                    color: white;
                    animation: pulse 2s infinite;
                }

                .progress-label {
                    font-size: 0.875rem;
                    color: #6b7280;
                    text-align: center;
                }

                .progress-step.completed .progress-label {
                    color: #10b981;
                    font-weight: 500;
                }

                .progress-step.active .progress-label {
                    color: #3b82f6;
                    font-weight: 500;
                }

                .transaction-info {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 1rem;
                    margin-bottom: 2rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .transaction-label {
                    font-weight: 500;
                    color: #475569;
                }

                .transaction-id {
                    font-family: 'Courier New', monospace;
                    font-weight: 600;
                    color: #1e293b;
                }

                .help-section {
                    margin-top: 2rem;
                    text-align: left;
                }

                .help-section h4 {
                    font-size: 1.125rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 1rem;
                }

                .help-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }

                .help-list li {
                    padding: 0.5rem 0;
                    color: #6b7280;
                    position: relative;
                    padding-left: 1.5rem;
                }

                .help-list li::before {
                    content: '•';
                    position: absolute;
                    left: 0;
                    color: #3b82f6;
                    font-weight: bold;
                }

                .result-card {
                    border-radius: 16px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                }

                .result-card.success {
                    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
                    border: 2px solid #bbf7d0;
                }

                .result-card.error {
                    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
                    border: 2px solid #fecaca;
                }

                .result-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 1rem;
                    text-align: left;
                }

                .result-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem;
                    background: rgba(255, 255, 255, 0.8);
                    border-radius: 12px;
                }

                .result-label {
                    font-weight: 500;
                    color: #374151;
                }

                .result-value {
                    font-weight: 600;
                    color: #1f2937;
                    text-align: right;
                }

                .template-section {
                    margin-top: 2rem;
                    text-align: left;
                }

                .template-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1rem;
                }

                .template-header h3 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #374151;
                    margin: 0;
                }

                .template-preview {
                    background: white;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 2rem;
                    font-family: 'Times New Roman', serif;
                    font-size: 0.875rem;
                    line-height: 1.5;
                    color: #000;
                    max-height: 400px;
                    overflow-y: auto;
                }

                .template-content {
                    max-width: 600px;
                    margin: 0 auto;
                }

                .template-header-content {
                    text-align: center;
                    margin-bottom: 1.5rem;
                }

                .template-header-content h4 {
                    font-size: 1.125rem;
                    font-weight: bold;
                    margin-bottom: 0.25rem;
                }

                .template-header-content p {
                    font-size: 0.875rem;
                    margin: 0;
                }

                .template-ref {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 1rem;
                    font-weight: bold;
                }

                .template-recipient {
                    margin-bottom: 1rem;
                    line-height: 1.4;
                }

                .template-subject {
                    text-align: center;
                    font-weight: bold;
                    text-decoration: underline;
                    margin: 1rem 0;
                }

                .template-body p {
                    margin: 1rem 0;
                }

                .certificate-table-container {
                    margin: 1rem 0;
                    overflow-x: auto;
                }

                .certificate-table-preview {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 0.75rem;
                }

                .certificate-table-preview th,
                .certificate-table-preview td {
                    border: 1px solid #000;
                    padding: 0.5rem;
                    text-align: left;
                }

                .certificate-table-preview th {
                    background-color: #f5f5f5;
                    font-weight: bold;
                }

                .btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    border: none;
                    border-radius: 12px;
                    font-size: 1rem;
                    font-weight: 600;
                    text-decoration: none;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    min-height: 48px;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-large {
                    padding: 1rem 2rem;
                    font-size: 1.125rem;
                    min-height: 56px;
                }

                .btn-sm {
                    padding: 0.5rem 1rem;
                    font-size: 0.875rem;
                    min-height: 40px;
                }

                .btn-primary {
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    color: white;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
                }

                .btn-primary:hover:not(:disabled) {
                    background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
                }

                .btn-secondary {
                    background: #f8fafc;
                    color: #475569;
                    border: 2px solid #e2e8f0;
                }

                .btn-secondary:hover:not(:disabled) {
                    background: #f1f5f9;
                    border-color: #cbd5e1;
                }

                .btn-success {
                    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                    color: white;
                    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
                }

                .btn-success:hover:not(:disabled) {
                    background: linear-gradient(135deg, #059669 0%, #047857 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
                }

                .btn-outline {
                    background: transparent;
                    color: #3b82f6;
                    border: 2px solid #3b82f6;
                }

                .btn-outline:hover:not(:disabled) {
                    background: #3b82f6;
                    color: white;
                }

                .btn-icon {
                    font-size: 1.125rem;
                }

                .button-group {
                    display: flex;
                    gap: 1rem;
                    margin-top: 2rem;
                }

                .button-group .btn {
                    flex: 1;
                }

                .spinner {
                    display: inline-block;
                    width: 1rem;
                    height: 1rem;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid currentColor;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }

                @media (max-width: 768px) {
                    .verification-step {
                        padding: 2rem 1rem;
                    }

                    .step-header h2 {
                        font-size: 1.5rem;
                    }

                    .step-header p {
                        font-size: 1rem;
                    }

                    .info-grid {
                        grid-template-columns: 1fr;
                    }

                    .result-grid {
                        grid-template-columns: 1fr;
                    }

                    .button-group {
                        flex-direction: column;
                    }

                    .template-header {
                        flex-direction: column;
                        gap: 1rem;
                        align-items: stretch;
                    }

                    .template-preview {
                        padding: 1rem;
                        font-size: 0.75rem;
                    }

                    .progress-indicator {
                        flex-direction: column;
                        gap: 1rem;
                    }

                    .progress-indicator::before {
                        display: none;
                    }

                    .progress-step {
                        flex-direction: row;
                        text-align: left;
                    }

                    .progress-icon {
                        margin-right: 1rem;
                        margin-bottom: 0;
                    }
                }

                .template-signature {
    margin-top: 2rem;
    border-top: 1px solid #e5e7eb;
    padding-top: 1rem;
}

.signature-container-preview {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: 1rem;
}

.signature-section-preview {
    flex: 1;
}

.signature-section-preview p {
    margin: 0.25rem 0;
    font-size: 0.75rem;
}

.stamp-section-preview {
    margin-left: 1rem;
    flex-shrink: 0;
}

@media (max-width: 768px) {
    .signature-container-preview {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }
    
    .stamp-section-preview {
        margin-left: 0;
        align-self: flex-end;
    }
}
    .signature-container {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: 40px;
    page-break-inside: avoid;
    break-inside: avoid;
}

.signature-section {
    flex: 1;
    page-break-inside: avoid;
    break-inside: avoid;
}

.stamp-section {
    margin-left: 20px;
    flex-shrink: 0;
    page-break-inside: avoid;
    break-inside: avoid;
}

@media print {
    body { 
        margin: 0; 
        font-size: 12px;
        line-height: 1.4;
    }
    .no-print { 
        display: none; 
    }
    .signature-container {
        page-break-inside: avoid;
        break-inside: avoid;
        margin-top: 20px;
    }
    .signature-section,
    .stamp-section {
        page-break-inside: avoid;
        break-inside: avoid;
    }
    @page {
        margin: 0.5in;
        size: A4;
    }

}
    .student-details-preview {
    margin: 1rem 0 1.5rem 0;
    padding: 1rem;
    background: rgba(248, 250, 252, 0.5);
    border-radius: 8px;
}

.detail-row {
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
}

.detail-row:last-child {
    margin-bottom: 0;
}
            `}</style>
        </div>
    );
};

export default VerificationForm;